import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'pg_cookie_consent_v1';

/**
 * POPIA / GDPR cookie consent banner.
 * - Strictly necessary cookies (auth) are always allowed (legal exemption).
 * - Anything optional (analytics in future) requires explicit opt-in.
 * - User's choice is stored locally and respected on subsequent visits.
 */
export function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Give the page a moment to load before banner appears (avoids CLS)
        setTimeout(() => setShow(true), 600);
      }
    } catch (_e) { /* ignore */ }
  }, []);

  const record = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        choice, at: new Date().toISOString(),
      }));
    } catch (_e) { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-40 bg-white border-2 border-brand-primary rounded-xl shadow-2xl p-4"
      data-testid="cookie-consent-banner"
    >
      <div className="flex items-start gap-3">
        <Cookie className="w-5 h-5 text-brand-signature flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm text-zinc-900 mb-1">Cookies on PipeGuide</h4>
          <p className="text-xs text-zinc-600 leading-relaxed mb-3">
            We use strictly-necessary cookies to keep you signed in. No analytics, no ads, no
            third-party trackers. Read our{' '}
            <a href="/privacy" className="text-brand-primary font-semibold hover:underline">
              Privacy Policy
            </a>{' '}
            for details.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => record('accept')}
              className="flex-1 text-xs font-semibold bg-brand-primary hover:bg-brand-hover text-white py-2 rounded-md"
              data-testid="cookie-consent-accept"
            >
              OK, got it
            </button>
            <a
              href="/privacy"
              className="flex-1 text-xs font-semibold border border-zinc-300 text-zinc-700 hover:bg-zinc-50 py-2 rounded-md text-center"
              data-testid="cookie-consent-learn-more"
            >
              Learn more
            </a>
          </div>
        </div>
        <button
          onClick={() => record('dismiss')}
          className="text-zinc-400 hover:text-zinc-600 flex-shrink-0"
          aria-label="Dismiss"
          data-testid="cookie-consent-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
